import { supervisedAssuranceIdentityVerificationRecord } from '@api/selfServiceServices';
import ContinueButton from '@components/continueButton/ContinueButton';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Checkbox, Form, Input } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EpcsSetupStep } from '../EpcsSetup';

enum FieldNames {
  ID_DOCUMENTS = 'idDocuments',
  COMMENTS = 'comments',
}

interface FormValues {
  [FieldNames.ID_DOCUMENTS]: string[];
  [FieldNames.COMMENTS]?: string;
}

interface VerifyIdentityProps {
  assuranceId: string;
  tenantId?: string;
  workflowId: string;
  setStep: (step: EpcsSetupStep) => void;
  cancelFlow: () => void;
}

const TRANSLATION = 'self-service.epcs-setup.verify-id';
const COMMENTS_MAX_LENGTH = 255;
const ID_DOCUMENT_OPTIONS = ['state-id', 'military-id', 'passport'] as const;

const EpcsStep2VerifyIdentity = ({
  assuranceId,
  tenantId,
  workflowId,
  setStep,
  cancelFlow,
}: VerifyIdentityProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const { emitError } = useErrorContext();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await supervisedAssuranceIdentityVerificationRecord(
          {
            assuranceId,
            idDocuments: values[FieldNames.ID_DOCUMENTS],
            comments: values[FieldNames.COMMENTS],
          },
          workflowId,
          tenantId,
        );
        setStep(EpcsSetupStep.STEP3_VERIFY_AUTHENTICATOR);
      } catch (error) {
        emitError(new AppError(error as ApiError));
      }
    },
    [emitError, assuranceId, tenantId, workflowId, setStep],
  );

  return (
    <Form<FormValues>
      className="continue-layout"
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
    >
      <div>
        <h2 className="title">{t(`${TRANSLATION}.title`)}</h2>
        <p>{t(`${TRANSLATION}.instructions`)}</p>

        <Form.Item name={FieldNames.ID_DOCUMENTS}>
          <Checkbox.Group
            style={{ flexDirection: 'column', gap: '.5rem' }}
            onChange={checkedValues => setIsSaveDisabled(checkedValues.length === 0)}
          >
            {ID_DOCUMENT_OPTIONS.map(option => (
              <Checkbox key={option} value={option}>
                {t(`${TRANSLATION}.${option}`)}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>

        <Form.Item name={FieldNames.COMMENTS} label={t(`${TRANSLATION}.comments`)}>
          <Input.TextArea rows={3} maxLength={COMMENTS_MAX_LENGTH} />
        </Form.Item>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          disabled={isSaveDisabled}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

export default EpcsStep2VerifyIdentity;
